import { IconMinus, IconPlus } from '@tabler/icons-react-native'
import { clsx } from 'clsx'
import { debounce, isNumber } from 'lodash'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { AccessibilityInfo, TextInput } from 'react-native'

import {
  FormInput,
  Icon,
  Input,
  InputField,
  Pressable,
  type FormInputProps,
  type InputFieldProps,
  type InputProps,
  type PressableProps
} from '@/components'

export interface NumberInputProps extends Omit<FormInputProps, 'children'> {
  decrementPressableProps?: Omit<PressableProps, 'disabled' | 'onPress'>
  displayValue?: string
  incrementPressableProps?: Omit<PressableProps, 'disabled' | 'onPress'>
  inputProps?: InputProps
  inputFieldProps?: Omit<InputFieldProps, 'onChange' | 'onChangeText' | 'value'>
  maxValue?: number
  minValue?: number
  onBlur?: () => void
  onChange: (value: number) => void
  value?: number | null | undefined
}

export type NumberInputRef = {
  focus: () => void
}

export const NumberInput = forwardRef<NumberInputRef, NumberInputProps>(
  (
    {
      accessibilityLabel: _accessibilityLabel,
      decrementPressableProps,
      displayValue,
      helper,
      incrementPressableProps,
      inputProps,
      inputFieldProps,
      isDisabled,
      isRequired,
      label,
      maxValue,
      minValue,
      onBlur,
      onChange,
      value: rawValue,
      ...props
    },
    ref
  ) => {
    const inputRef = useRef<TextInput>(null)
    const hasMax = isNumber(maxValue)
    const hasMin = isNumber(minValue)
    const [value, setValue] = useState(0)
    const accessibilityLabel = useMemo(
      () => `${_accessibilityLabel ?? `${label}${isRequired ? '. required' : ''}${helper ? `. ${helper}` : ''}`}`,
      [_accessibilityLabel, helper, isRequired, label]
    )
    const debouncedOnChange = useMemo(
      () =>
        debounce(
          (val: number) => {
            onChange(val)
            AccessibilityInfo.announceForAccessibility(`Value is now ${val}`)
          },
          250,
          { leading: false }
        ),
      [onChange]
    )
    const handleChange = useCallback(
      (nextValue: number) => {
        setValue(nextValue)
        debouncedOnChange(nextValue)
      },
      [debouncedOnChange]
    )

    useEffect(() => {
      setValue(rawValue ?? 0)
    }, [rawValue])

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus()
    }))

    return (
      <FormInput
        helper={helper}
        isDisabled={isDisabled}
        isRequired={isRequired}
        label={label}
        {...props}
      >
        <Input
          {...inputProps}
          className={clsx('w-32 rounded-lg', inputProps?.className)}
        >
          <Pressable
            accessible={false}
            accessibilityElementsHidden
            aria-hidden
            className='h-full w-10 items-center justify-center'
            disabled={isDisabled || (hasMin && value <= minValue)}
            focusable={false}
            importantForAccessibility='no-hide-descendants'
            onPress={useCallback(() => handleChange(value - 1), [handleChange, value])}
            tabIndex={-1}
            {...decrementPressableProps}
          >
            <Icon
              as={IconMinus}
              className='mx-1 color-white'
              size='md'
            />
          </Pressable>
          <InputField
            accessibilityLabel={accessibilityLabel}
            accessibilityState={{
              disabled: isDisabled
            }}
            aria-disabled={isDisabled}
            aria-label={accessibilityLabel}
            aria-required={isRequired}
            autoCapitalize='none'
            autoComplete='off'
            autoCorrect={false}
            className='h-full w-full px-0 text-center font-poppinsMedium text-base color-white'
            keyboardType='number-pad'
            onBlur={onBlur}
            onChangeText={useCallback(
              (value: string) => handleChange(Number(value.replace(/[^0-9]/g, ''))),
              [handleChange]
            )}
            // @ts-expect-error because gluestack types are stupid
            ref={inputRef}
            returnKeyType='done'
            submitBehavior='submit'
            tabIndex={0}
            value={displayValue ?? String(value)}
            {...inputFieldProps}
          />
          <Pressable
            accessible={false}
            accessibilityElementsHidden
            aria-hidden
            className='h-full w-10 items-center justify-center'
            disabled={isDisabled || (hasMax && value >= maxValue)}
            focusable={false}
            importantForAccessibility='no-hide-descendants'
            onPress={useCallback(() => handleChange(value + 1), [handleChange, value])}
            tabIndex={-1}
            {...incrementPressableProps}
          >
            <Icon
              as={IconPlus}
              className='mx-1 color-white'
              size='sm'
            />
          </Pressable>
        </Input>
      </FormInput>
    )
  }
)

NumberInput.displayName = 'NumberInput'
